import React, { useEffect, useState } from 'react'
import { DashboardService } from '../../../../Api/EmploymentEquity/Dashboard/DashboardService';
import { EmploymentEquityDashboardDto } from '../../../../Model/Dto/EmploymentEquity/Dashboard';
import { GenderList } from '../../../../Model/Enum/MultiSelectOptions/GenderOptions';
import { OccupationalLevelDisplayList } from '../../../../Model/Enum/MultiSelectOptions/OccupationallevelOptions';
import { RaceList } from '../../../../Model/Enum/MultiSelectOptions/RaceOptions';
import { mapMovementHeadcountToDoubleColumnChartData } from '../../../../Model/Mappers/EmploymentEquityMapper';
import { IMultiSelectOptions } from '../../MultiSelect/MultiSelect';
import { ChartFilter } from '../../Popovers/ChartFilter';
import { DoubleColumnChart } from '../Base/DoubleColumnChart/DoubleColumnChart';
import { AlertMessage } from '../../Alerts/alertMessage';
import { Box, ClickAwayListener, Dialog, DialogContent, IconButton, Popper } from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { MDBox } from '../../MDBox';
import { MDButton } from '../../MDButton';
import { MovementsDrilldown } from '../../../../../Modules/EmploymentEquity/WorkforceProfile_EEA2/Movements/MovementsDrilldown';
import { Gender, MovementTypes, OccupationalLevelTypes, RaceTypes } from '../../../../Model/Enum/BEE123Enums';
import { CustomDialogTitle } from '../../../../../Modules/Common/Custom/CustomDialogTitle';
import { userFriendlyDate } from '../../../../../Helpers/DateFormatter';
import { EEOccupationalLevel } from '../../../../Model/Enum/EmploymentEquity';

interface IProps {
    scorecardId?: string;
    scorecardTypeId?: string;
    chartOptions?: any;
    dashboardLevelOnClick?: (chartLabel: string) => void;
    movementType: MovementTypes;
    sourceScorecardIds?: string[];
}

export const MovementHeadcountChart = (props: IProps) => {
    const [occupationLevelIds, setOccupationLevelIds] = useState(OccupationalLevelDisplayList());
    const [raceIds, setRaceIds] = useState(RaceList);
    const [genderIds, setGenderIds] = useState(GenderList);
    const [computedHash, setComputedHash] = useState('');
    const [isStacked, setIsStacked] = useState(props.chartOptions.isStacked ?? true);
    const [counter, setCounter] = useState(0);
    const [anchorElFilter, setAnchorElFilter] = React.useState<null | HTMLElement>(null);

    const [showDrillDown, setShowDrillDown] = React.useState<boolean>(false);
    const [isPermanent, setIsPermanent] = React.useState<boolean>();
    const [occupationId, setOccupationId] = React.useState<number>();
    const [eeOccupationId, setEEOccupationId] = React.useState<number>();
    const [raceId, setRaceId] = React.useState<number>();
    const [genderId, setGenderId] = React.useState<number>();
    const [openFilter, setOpenFilter] = React.useState(false);

    useEffect(() => {
        getChartData();
        setCounter(counter + 1);
    }, [props.sourceScorecardIds]); // eslint-disable-line react-hooks/exhaustive-deps

    const filterClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElFilter(anchorElFilter ? null : event.currentTarget);
        setOpenFilter(true);
    };

    const onChartSegmentClick = (finalLabel: string) => {
        buildSearchArraysFromLabel(finalLabel);
    }

    const buildSearchArraysFromLabel = (label: string) => {
        label = label.toLowerCase();
        setIsPermanent(undefined);
        setOccupationId(undefined);
        setEEOccupationId(undefined);
        setRaceId(undefined);
        setGenderId(undefined);

        //gender
        if (label.includes("female")) {
            setGenderId(Gender.Female);
        }
        else if (label.includes("male")) {
            setGenderId(Gender.Male);
        }

        //race
        if (label.includes("nonsa") || label.includes("nonrsa")) {
            setRaceId(RaceTypes.NonRSA);
        }
        else if (label.includes("african")) {
            setRaceId(RaceTypes.African);
        }
        else if (label.includes("coloured")) {
            setRaceId(RaceTypes.Coloured);
        }
        else if (label.includes("indian")) {
            setRaceId(RaceTypes.Indian);
        }
        else if (label.includes("white")) {
            setRaceId(RaceTypes.White);
        }

        if (label.includes("top")) {
            setEEOccupationId(EEOccupationalLevel.Top_Management);
        }
        else if (label.includes("senior")) {
            setEEOccupationId(EEOccupationalLevel.Senior_Management);
        }
        else if (label.includes("mid")) {
            setEEOccupationId(EEOccupationalLevel.Middle_Management);
        }
        else if (label.includes("junior")) {
            setEEOccupationId(EEOccupationalLevel.Junior_Management);
        }
        else if (label.includes("semi")) {
            setEEOccupationId(EEOccupationalLevel.Semi_Skilled);
        }
        else if (label.includes("unskilled")) {
            setEEOccupationId(EEOccupationalLevel.Unskilled);
        }

        if (label.includes('temporary')) {
            setIsPermanent(false);
        }
        else if (label.includes('permanent')) {
            setIsPermanent(true);
        }

        setShowDrillDown(true);
    }

    const changeStackedView = () => {
        setIsStacked(isStacked ? false : true);
    }

    const updateGenderFilter = (selectedGenderFilterOptions: Array<IMultiSelectOptions>) => {
        setGenderIds(selectedGenderFilterOptions);
        getChartData();
        setCounter(counter + 1);
    }

    const updateRaceFilter = (selectedRaceFilterOptions: Array<IMultiSelectOptions>) => {
        setRaceIds(selectedRaceFilterOptions);
        getChartData();
        setCounter(counter + 1);
    }

    const updateOccupationLevelFilter = (selectedOccupationLevelFilterOptions: Array<IMultiSelectOptions>) => {
        setOccupationLevelIds(selectedOccupationLevelFilterOptions);
        getChartData();
        setCounter(counter + 1);
    }

    const resetChartFilters = () => {
        setOccupationLevelIds(OccupationalLevelDisplayList());
        setRaceIds(RaceList);
        setGenderIds(GenderList);
        getChartData();
        setAnchorElFilter(null);
        setOpenFilter(false);
        setCounter(counter + 1);
    }

    const closeChartFilter = () => {
        setAnchorElFilter(null);
        setOpenFilter(false);
    }

    const getChartData = () => {
        let SelectedGenderArray: Array<number> = [];
        let SelectedRaceArray: Array<number> = [];
        let SelectedOccupationalLevelArray: Array<number> = [];
        genderIds.map((item) => SelectedGenderArray.push(parseInt(item.value)));
        raceIds.map((item) => SelectedRaceArray.push(parseInt(item.value)));
        occupationLevelIds.map((item) => SelectedOccupationalLevelArray.push(parseInt(item.value)));
        if(SelectedOccupationalLevelArray.includes(OccupationalLevelTypes.Top_Management)){
          SelectedOccupationalLevelArray = SelectedOccupationalLevelArray.filter(function(item) {
              return item !== OccupationalLevelTypes.Top_Management
          });
          SelectedOccupationalLevelArray.push(OccupationalLevelTypes.Executive_Director);
          SelectedOccupationalLevelArray.push(OccupationalLevelTypes.Other_Executive_Management);
        }
        const data: EmploymentEquityDashboardDto.MovementHeadcountRequest = {
            occupationLevelIds: SelectedOccupationalLevelArray,
            raceIds: SelectedRaceArray,
            genderIds: SelectedGenderArray,
            scorecardId: props.scorecardId,
            MovementType: props.movementType,
            sourceScorecardIds: props.sourceScorecardIds ? props.sourceScorecardIds : undefined
        }
        return DashboardService.GetMovementHeadCount(data).then(
            mapMovementHeadcountToDoubleColumnChartData(raceIds, genderIds, occupationLevelIds, props.chartOptions.isPowerPointPrint)
        );

    }

    const getDialogTitle = () => {
        switch (props.movementType) {
            case MovementTypes.Recruitment:
                return "Recruitment Drilldown";

            case MovementTypes.Promotion:
                return "Promotion Drilldown";

            case MovementTypes.Termination:
                return "Termination Drilldown";
            default:
                return "Movement Drilldown";
        }
    }

    const renderFilters = () => {
        return <MDBox style={{ float: 'right' }}>
            <IconButton
                color="primary"
                onClick={filterClick} >
                <FilterAltIcon />
            </IconButton>
            {openFilter && <ClickAwayListener onClickAway={() => closeChartFilter()}>
                <Popper
                    open={openFilter}
                    anchorEl={anchorElFilter}
                    placement="bottom-end"
                >
                    <Box sx={{
                        bgcolor: 'background.paper',
                        boxShadow: 2,
                        borderRadius: 3,
                        p: 2,
                        border: '1px solid rgba(0, 0, 0, 0.25)',
                        maxWidth: 530,
                        minWidth: 400
                    }}>
                        <ChartFilter
                            resetChartFilters={() => { resetChartFilters() }}
                            enableResetFilter={true}
                            dropdowns={[
                                {
                                    label: "Gender",
                                    options: GenderList,
                                    selected: genderIds,
                                    onChange: updateGenderFilter,
                                },
                                {
                                    label: "Race",
                                    options: RaceList,
                                    selected: raceIds,
                                    onChange: updateRaceFilter,
                                },
                                {
                                    label: "Occupational Level",
                                    options: OccupationalLevelDisplayList(),
                                    selected: occupationLevelIds,
                                    onChange: updateOccupationLevelFilter,
                                },
                            ]}
                        ></ChartFilter>

                    </Box>
                </Popper>
            </ClickAwayListener>
            }
        </MDBox>
    }

    const newHash = computedHash;
    return (
        <>
            <MDBox>
                <MDBox sx={{ margin: 1 }}>
                    {renderFilters()}
                    <MDButton color='primary' style={{ float: 'right' }}
                        onClick={() => { changeStackedView() }}
                    >
                        {isStacked ? "Column View" : "Stacked View"}
                    </MDButton>
                    <br />
                    <br />
                    <MDBox>
                        {
                            genderIds.length > 0 &&
                            raceIds.length > 0 &&
                            occupationLevelIds.length > 0 && (
                                <DoubleColumnChart
                                    key={counter}
                                    hash={newHash}
                                    chartOptions={props.chartOptions}
                                    fetchData={() => getChartData()}
                                    onSegmentClick={onChartSegmentClick}
                                    isStacked={isStacked}
                                />
                            )
                        }
                    </MDBox>
                    {
                        genderIds.length == 0 && (
                            <AlertMessage
                                type="danger"
                                message="Please select atleast one Gender"
                            />
                        )}
                    {raceIds.length == 0 && (
                        <AlertMessage
                            type="danger"
                            message="Please select atleast one Race"
                        />
                    )
                    }
                    {
                        occupationLevelIds.length == 0 && (
                            <AlertMessage
                                type="danger"
                                message="Please select atleast one Occupational Level"
                            />
                        )
                    }
                </MDBox>
                <MDBox sx={{ margin: 1 }}>
                    {
                        props.chartOptions?.scorecardEndDate &&
                        <span className="bottom-of-chart inline-block"><i style={{ color: 'black' }}>For the period ending on </i> &nbsp;<b>{userFriendlyDate(props.chartOptions?.scorecardEndDate)}</b></span>
                    }
                </MDBox>
            </MDBox>

            {/* drill down */}
            <Dialog open={showDrillDown} onClose={() => setShowDrillDown(false)} aria-labelledby="form-dialog-title" maxWidth={false} sx={{ minWidth: "1200px" }}>
                <CustomDialogTitle id="customized-dialog-title" onClose={() => setShowDrillDown(false)}>
                    {getDialogTitle()}
                </CustomDialogTitle>
                <DialogContent>
                    <MovementsDrilldown 
                        scorecardId={props.scorecardId!} 
                        raceId={raceId} 
                        genderId={genderId} 
                        isPermanent={isPermanent} 
                        movementType={props.movementType} 
                        eeOccupationId={eeOccupationId} 
                        sourceScorecardIds={props.sourceScorecardIds ? props.sourceScorecardIds : undefined}/>
                </DialogContent>
            </Dialog>
        </>
    )
}

